import type { FieldResult, PageResult } from '../shared/desktop-api';

const numericKey = (value: string): string | null => {
  let plain = value.trim().replace(/^\$\s*/, '');
  const accountingNegative = /^\(\s*\$?\s*\d[\d,]*(?:\.\d+)?\s*\)$/.test(plain);
  if (accountingNegative)
    plain = `-${plain
      .slice(1, -1)
      .trim()
      .replace(/^\$\s*/, '')}`;
  plain = plain.replace(/,/g, '');
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(plain)) return null;
  const [integer, fraction = ''] = plain.replace(/^\+/, '').split('.');
  return `${integer.replace(/^(-?)0+(?=\d)/, '$1')}.${fraction.replace(/0+$/, '')}`;
};
const containsNumber = (text: string, key: string) =>
  // Consume accounting parentheses as part of the amount: a positive value
  // must never recover a link by matching only the digits inside a credit.
  [
    ...text.matchAll(
      /(?<![\p{L}\p{N}.,($+\-])(?:\$\s*)?(?:\(\s*\$?\s*\d[\d,]*(?:\.\d+)?\s*\)|[-+]?\d[\d,]*(?:\.\d+)?)(?![\p{L}\p{N}.,)])/gu,
    ),
  ].some((match) => numericKey(match[0]) === key);

/** Repairs only the displayed source link of retained numeric answers. No value,
 * confidence, confirmation, or learned evidence is changed. Ambiguity has no box. */
export function numericFieldDisplayEvidence(field: FieldResult, pages: PageResult[]): FieldResult {
  const key = numericKey(field.value);
  if (!key || field.barcodeEvidence) return field;
  const lines = pages.flatMap((page) => {
    const rows = page.lines.length
      ? page.lines.map((line, index) => ({ index, text: line.text }))
      : [...new Set(page.words.map((word) => word.lineIndex))].map((index) => ({
          index,
          text: page.words
            .filter((w) => w.lineIndex === index)
            .sort((a, b) => a.box.x - b.box.x)
            .map((w) => w.text)
            .join(' '),
        }));
    return rows.map((line) => ({ ...line, page: page.number }));
  });
  if (
    lines.some(
      (line) =>
        line.page === field.evidencePageNumber &&
        field.evidenceLineIndexes?.includes(line.index) &&
        containsNumber(line.text, key),
    )
  )
    return field;
  const matches = lines.filter((line) => containsNumber(line.text, key));
  return {
    ...field,
    evidencePageNumber: matches.length === 1 ? matches[0].page : field.evidencePageNumber,
    evidenceLineIndexes: matches.length === 1 ? [matches[0].index] : [],
  };
}
